import { supabase } from '../../config/supabase';
import { DynamicChangelogService, DynamicChangelog } from './dynamic-changelog.service';

export class ChangelogService {
  static async getLastSeenVersion(): Promise<string> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return '1.0.0';

    const { data, error } = await supabase
      .from('users')
      .select('last_seen_changelog_version, created_at')
      .eq('id', user.id)
      .single();

    if (error || !data) return '1.0.0';
    
    // Pour un nouvel utilisateur (inscrit depuis moins de 24h), 
    // on considère qu'il a déjà "vu" les changelogs existants
    if (!data.last_seen_changelog_version && data.created_at) {
      const userCreatedAt = new Date(data.created_at);
      const now = new Date();
      const hoursSinceCreation = (now.getTime() - userCreatedAt.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceCreation < 24) {
        // Nouvel utilisateur : on marque comme ayant vu la version actuelle
        const currentChangelog = await DynamicChangelogService.getActiveChangelog();
        if (currentChangelog) {
          await this.updateLastSeenVersion(currentChangelog.version);
          return currentChangelog.version;
        }
      }
    }
    
    return data.last_seen_changelog_version || '1.0.0';
  }

  static async updateLastSeenVersion(version: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utilisateur non authentifié');

    const { error } = await supabase
      .from('users')
      .update({ last_seen_changelog_version: version })
      .eq('id', user.id);

    if (error) throw error;
  }

  static async getCurrentChangelog(): Promise<DynamicChangelog | null> {
    try {
      return await DynamicChangelogService.getActiveChangelog();
    } catch (error) {
      console.error('Erreur lors de la récupération du changelog:', error);
      return null;
    }
  }

  static async shouldShowChangelog(): Promise<boolean> {
    const [lastSeenVersion, currentChangelog] = await Promise.all([
      this.getLastSeenVersion(),
      this.getCurrentChangelog()
    ]);

    if (!currentChangelog) return false;
    return lastSeenVersion !== currentChangelog.version;
  }

  // Alternative pour le stockage local si besoin
  static getLastSeenVersionLocal(): string {
    return localStorage.getItem('last_seen_changelog_version') || '1.0.0';
  }

  static updateLastSeenVersionLocal(version: string): void {
    localStorage.setItem('last_seen_changelog_version', version);
  }

  static shouldShowChangelogLocal(currentVersion: string): boolean {
    const lastSeenVersion = this.getLastSeenVersionLocal();
    return lastSeenVersion !== currentVersion;
  }
}
